<template>
  <div class="date-field">
    <v-text-field
      ref="field"
      outlined
      color="brandPrimary"
      append-icon="mdi-calendar"
      hide-details="auto"
      :label="label"
      :placeholder="placeholder"
      :hint="hint || undefined"
      :persistent-hint="!!hint"
      :value="text"
      :rules="fieldRules"
      @input="text = $event"
      @blur="commit"
      @keydown.enter.prevent="commit"
      @click:append="openPicker"
    />
    <input
      ref="nativeInput"
      type="datetime-local"
      class="native-picker"
      tabindex="-1"
      aria-hidden="true"
      :value="value"
      :min="min || undefined"
      :max="max || undefined"
      @input="onPick($event.target.value)"
    />
  </div>
</template>

<script>
import moment from "moment";

const DISPLAY_FORMAT = "MMM D, YYYY, h:mm A";
const VALUE_FORMAT = "YYYY-MM-DDTHH:mm";
const DEFAULT_PLACEHOLDER = "Jan 5, 2026, 9:00 AM";

// Accepted when a date is typed by hand. Every shape is listed both padded and
// unpadded, and with and without a time, because strict parsing (which is what
// keeps moment from reading "garbage" as a date) treats "8/10" and "08/10" as
// different formats.
const DATE_SHAPES = ["MMM D, YYYY", "M/D/YYYY", "MM/DD/YYYY", "M/D/YY", "MM/DD/YY", "YYYY-MM-DD"];
const TIME_SHAPES = ["h:mm A", "hh:mm A", "H:mm", "HH:mm"];
const INPUT_FORMATS = [
  VALUE_FORMAT,
  ...DATE_SHAPES.flatMap((date) => [
    date,
    ...TIME_SHAPES.flatMap((time) => [`${date} ${time}`, `${date}, ${time}`]),
  ]),
];

export default {
  name: "DateTimeField",
  props: {
    value: {
      type: String,
      default: null,
    },
    label: {
      type: String,
      default: "",
    },
    hint: {
      type: String,
      default: "",
    },
    placeholder: {
      type: String,
      default: DEFAULT_PLACEHOLDER,
    },
    rules: {
      type: Array,
      default: () => [],
    },
    min: {
      type: String,
      default: null,
    },
    max: {
      type: String,
      default: null,
    },
  },
  emits: ["input"],
  data() {
    return {
      text: "",
    };
  },
  computed: {
    fieldRules() {
      return [
        () => !this.text.trim() || !!this.toValue(this.text) || this.formatError,
        ...this.rules,
      ];
    },
    formatError() {
      return `Enter a date like ${this.placeholder}`;
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(value) {
        this.text = this.toDisplay(value);
      },
    },
  },
  methods: {
    toDisplay(value) {
      return value ? moment(value).format(DISPLAY_FORMAT) : "";
    },
    // Returns a VALUE_FORMAT string, or null when the text is empty or unparseable.
    // A date typed without a time lands at 12:00 AM, which is moment's default.
    toValue(text) {
      const trimmed = (text || "").trim();
      if (!trimmed) return null;
      const parsed = moment(trimmed, INPUT_FORMATS, true);
      return parsed.isValid() ? parsed.format(VALUE_FORMAT) : null;
    },
    commit() {
      const next = this.toValue(this.text);
      if (next) this.text = this.toDisplay(next);
      this.$emit("input", next);
    },
    onPick(value) {
      this.$emit("input", value || null);
    },
    validate(force = false) {
      return this.$refs.field?.validate(force);
    },
    // Opens the browser's native calendar+time picker directly. Requires a
    // user gesture (the click on the calendar icon) and showPicker() support
    // (Chromium 99+, Safari 16+, Firefox 101+). Falls back to focus() otherwise.
    openPicker() {
      const el = this.$refs.nativeInput;
      if (!el) return;
      if (typeof el.showPicker === "function") {
        try {
          el.showPicker();
          return;
        } catch (e) {
          /* not allowed — fall through to focus */
        }
      }
      el.focus();
    },
  },
};
</script>

<style scoped>
.date-field {
  position: relative;
}

/* The native control is invisible but still the anchor the picker pops out from,
   so it has to cover the visible field box (56px, Vuetify's outlined height).
   Sizing it smaller detaches the popup from the field, which is what misplaced
   the End date picker in Firefox. */
.native-picker {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 56px;
  opacity: 0;
  pointer-events: none;
}
</style>
